import {useLazyQuery} from '@apollo/client';
import {useCallback} from 'react';
import {formatTimeRangeObjectToISO} from '../../../../utils/dateTimeUtils';
import {PodReviewType, PodReviewFilters, PodReviewTabType} from '../graphql/types/PodReviewType';
import {
    GetPodReviewOrdersResponse,
    GET_POD_REVIEW_ORDERS,
    GetPodReviewOrdersInput,
    PodReviewFiltersTypeInput
} from '../graphql/queries/getPodReviewOrders';

export interface PodReviewQueryResult {
    podReviewData: PodReviewType[];
    loading: boolean;
    executeQuery: (filters: PodReviewFilters, page: number, size: number, tab: PodReviewTabType) => void;
    totalElements: number;
    error?: any;
}

// 构建筛选条件输入（供count查询和列表查询共用）
export const buildFiltersInputFromFilters = (filters: PodReviewFilters): PodReviewFiltersTypeInput | undefined => {
    if (
        !filters.statusUpdatedAt &&
        !filters.podFirstUploadTime &&
        !filters.pickupHubSortingCodes?.length &&
        !filters.destWarehouseIds?.length &&
        !filters.areaSortingCodeList?.length &&
        !filters.deliveredDspIds?.length &&
        !filters.deliveredDriverAccountId &&
        !filters.podReviewStatus &&
        !filters.trackingNumbers &&
        !filters.channelOrderNumbers &&
        !filters.reviewerId &&
        !filters.failureReasons?.length &&
        !filters.reviewTime
    ) {
        return undefined;
    }

    const pickupHubSortingCodes = filters.pickupHubSortingCodes?.filter((code): code is string => !!code);
    const destWarehouseIds = filters.destWarehouseIds?.filter((id): id is string => !!id);
    const areaSortingCodeList = filters.areaSortingCodeList?.filter((code): code is string => !!code);
    const deliveredDspIds = filters.deliveredDspIds?.filter((id): id is string => !!id);

    return {
        statusUpdatedAt: formatTimeRangeObjectToISO(filters.statusUpdatedAt),
        podFirstUploadTime: formatTimeRangeObjectToISO(filters.podFirstUploadTime),
        pickupHubSortingCodes,
        pickupHubSortingCode: pickupHubSortingCodes && pickupHubSortingCodes.length === 1 ? pickupHubSortingCodes[0] : undefined,
        destWarehouseIds,
        destWarehouseId: destWarehouseIds && destWarehouseIds.length === 1 ? destWarehouseIds[0] : undefined,
        areaSortingCodeList,
        deliveredDspIds,
        deliveredDspId: deliveredDspIds && deliveredDspIds.length === 1 ? deliveredDspIds[0] : undefined,
        deliveredDriverAccountId: filters.deliveredDriverAccountId,
        podReviewStatus: filters.podReviewStatus,
        trackingNumbers: filters.trackingNumbers,
        channelOrderNumbers: filters.channelOrderNumbers,
        reviewerId: filters.reviewerId,
        failureReasons: filters.failureReasons?.filter((reason): reason is string => !!reason),
        reviewTime: formatTimeRangeObjectToISO(filters.reviewTime)
    };
};

const buildQueryVariables = (filters: PodReviewFilters, page: number, size: number, tab: PodReviewTabType): GetPodReviewOrdersInput => {
    return {
        pageRequest: {
            page,
            size,
            sortBy: "updatedAt",
            sortDirection: "DESC"
        },
        tabType: tab,
        filters: buildFiltersInputFromFilters(filters)
    };
};

export const usePodReviewQuery = (): PodReviewQueryResult => {

    const [execute, {data, loading, error}] = useLazyQuery<GetPodReviewOrdersResponse>(
        GET_POD_REVIEW_ORDERS,
        {
            fetchPolicy: 'network-only',
            nextFetchPolicy: 'network-only',
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true,
        }
    );

    const executeQuery = useCallback((filters: PodReviewFilters, page: number, size: number, tab: PodReviewTabType) => {
        execute({
            variables: buildQueryVariables(filters, page, size, tab),
        });
    }, [execute]);

    const podReviewData = data?.getPodReviewPageList?.content || [];
    const totalElements = data?.getPodReviewPageList?.totalElements || 0;

    return {
        podReviewData,
        loading,
        executeQuery,
        totalElements,
        error
    };
};
